class MarqueePlayPauseButton extends HTMLElement {
  static USER_PAUSED_CLASS = "marquee--user-paused";

  /* ------------ Constructor & bound handlers ------------ */
  constructor() {
    super();

    this._boundHandleClick = this.handleClick.bind(this);
    this._boundHandleKeyDown = this.handleKeyDown.bind(this);
  }

  /* ------------ Custom element lifecycle ------------ */
  connectedCallback() {
    if (this._lifecycleConnected) return;

    this._lifecycleConnected = true;

    if (!this.hasAttribute("role")) {
      this.setAttribute("role", "button");
    }

    if (!this.hasAttribute("tabindex")) {
      this.setAttribute("tabindex", "0");
    }

    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    this._lifecycleConnected = false;
  }

  /* ------------ Initial wiring & cleanup ------------ */
  setup() {
    const marquee = this.resolveMarquee();

    if (!marquee) {
      this.hidden = true;

      return;
    }

    this.hidden = false;

    const paused = this.isMarqueePaused(marquee);

    this.setUiPaused(paused);

    this.addEventListener("click", this._boundHandleClick);
    this.addEventListener("keydown", this._boundHandleKeyDown);
  }

  teardown() {
    this.removeEventListener("click", this._boundHandleClick);
    this.removeEventListener("keydown", this._boundHandleKeyDown);
  }

  /* ------------ Marquee resolution & state ------------ */
  resolveMarquee() {
    return this.closest(".marquee");
  }

  isMarqueePaused(marquee) {
    return marquee.classList.contains(MarqueePlayPauseButton.USER_PAUSED_CLASS);
  }

  setMarqueePaused(marquee, paused) {
    marquee.classList.toggle(MarqueePlayPauseButton.USER_PAUSED_CLASS, paused);
  }

  /* ------------ Paused UI (matches video-play-pause pattern) ------------ */
  setUiPaused(paused) {
    const pauseText = this.dataset.labelPause || "Pause marquee";
    const playText = this.dataset.labelPlay || "Play marquee";

    this.classList.toggle("video-play-pause-button--paused", paused);
    this.setAttribute("aria-pressed", paused ? "false" : "true");
    this.setAttribute("aria-label", paused ? playText : pauseText);
  }

  /* ------------ User interaction ------------ */
  handleClick(event) {
    event.preventDefault();

    if (this.hasAttribute("disabled")) return;

    const marquee = this.resolveMarquee();

    if (!marquee) return;

    const nextPaused = !this.isMarqueePaused(marquee);

    this.setMarqueePaused(marquee, nextPaused);
    this.setUiPaused(nextPaused);
  }

  handleKeyDown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    if (this.hasAttribute("disabled")) return;

    event.preventDefault();
    this.handleClick(event);
  }
}

customElements.define("marquee-play-pause-button", MarqueePlayPauseButton);
